import { useState, useEffect } from "react";
import { Card, Row, Col, Typography, Divider, Button, Modal, Form, Input, Spin, Alert, message } from "antd";
import { UserOutlined, MessageOutlined, EditOutlined, LoadingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../lib/firebase";
import { userServices } from "../../lib/databaseService";
import { userApplicationServices } from "../../lib/databaseService";
import ProfileFormsTable from "./ProfileFormsTable";

const { Title, Text } = Typography;

export default function ProfilePage() {
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const [userData, setUserData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [applicationStats, setApplicationStats] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [form] = Form.useForm();

  // Load user data and applications
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        
        // Load user profile data
        const userProfile = await userServices.getUserFromFirestore(user.uid);
        setUserData(userProfile);

        // Load user applications
        const userApplications = await userApplicationServices.getUserApplications(user.uid);
        setApplications(userApplications);

        // Load application statistics
        const stats = await userApplicationServices.getUserApplicationStats(user.uid);
        setApplicationStats(stats);

      } catch (error) {
        console.error('Error loading user data:', error);
        message.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user]);

  const handleEditClick = () => {
    if (userData) {
      form.setFieldsValue({
        displayName: userData.displayName,
        email: userData.email,
        phoneNumber: userData.phoneNumber
      });
    }
    setIsModalOpen(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setUpdating(true);

      // Update user profile in Firebase
      const result = await userServices.updateUserProfile(user.uid, {
        displayName: values.displayName,
        phoneNumber: values.phoneNumber,
        photoURL: userData?.photoURL || '' // Keep existing photo URL
      });

      if (result.success) {
        // Update local state
        setUserData(prev => ({
          ...prev,
          displayName: values.displayName,
          phoneNumber: values.phoneNumber
        }));
        
        message.success('Profile updated successfully!');
        setIsModalOpen(false);
      } else {
        message.error(`Update failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Row justify="center" style={{ padding: "30px" }}>
        <Col xs={24} md={20} lg={16}>
          <Card bordered style={{ borderRadius: 10, textAlign: 'center', padding: '40px' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <div style={{ marginTop: 16 }}>
              <Text>Loading...</Text>
            </div>
          </Card>
        </Col>
      </Row>
    );
  }

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <Row justify="center" style={{ padding: "30px" }}>
        <Col xs={24} md={20} lg={16}>
          <Card bordered style={{ borderRadius: 10 }}>
            <Alert
              message="Authentication Required"
              description="Please sign in to view your profile."
              type="warning"
              showIcon
              action={
                <Button 
                  type="primary" 
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
              }
            />
          </Card>
        </Col>
      </Row>
    );
  }

  // Show loading while fetching data
  if (loading) {
    return (
      <Row justify="center" style={{ padding: "30px" }}>
        <Col xs={24} md={20} lg={16}>
          <Card bordered style={{ borderRadius: 10, textAlign: 'center', padding: '40px' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <div style={{ marginTop: 16 }}>
              <Text>Loading your profile...</Text>
            </div>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row justify="center" style={{ padding: "30px" }}>
      <Col xs={24} md={20} lg={16}>
        <Card bordered style={{ borderRadius: 10 }}>
          {/* Profile Header */}
          <Row gutter={[20, 20]} align="middle">
            <Col flex={1}>
              <Row align="middle" gutter={8}>
                <Col>
                  <Title level={3} style={{ marginBottom: 4 }}>
                    {userData?.displayName || 'No Name Set'}
                  </Title>
                </Col>
                <Col>
                  <EditOutlined
                    style={{ fontSize: 18, cursor: "pointer", color: "#1890ff" }}
                    onClick={handleEditClick}
                  />
                </Col>
              </Row>
              
              <Text strong>Email: </Text>
              <Text>{userData?.email || 'No email'}</Text> 
              <br />
              
              <Text strong>Contact: </Text>
              <Text>{userData?.phoneNumber || 'No contact number'}</Text>
              <br />
              
              <Text strong>Member since: </Text>
              <Text>{userData?.createdAt?.toLocaleDateString() || 'Unknown'}</Text>
            </Col>

            <Col>
              <Button
                type="primary"
                icon={<MessageOutlined />}
                onClick={() => navigate("/chat")}
                size="large"
              >
                Start Chat with Admin
              </Button>
            </Col>
          </Row>

          <Divider />

          {/* Application Statistics */}
          {applicationStats && (
            <div style={{ marginBottom: 24 }}>
              <Title level={4}>Application Statistics</Title>
              <Row gutter={16}>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ textAlign: 'center', background: '#f0f8ff' }}>
                    <Title level={2} style={{ color: '#1890ff', margin: 0 }}>
                      {applicationStats.total}
                    </Title>
                    <Text>Total Applications</Text>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ textAlign: 'center', background: '#fff7e6' }}>
                    <Title level={2} style={{ color: '#fa8c16', margin: 0 }}>
                      {applicationStats.pending}
                    </Title>
                    <Text>Pending</Text>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ textAlign: 'center', background: '#f6ffed' }}>
                    <Title level={2} style={{ color: '#52c41a', margin: 0 }}>
                      {applicationStats.reviewed}
                    </Title>
                    <Text>Reviewed</Text>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ textAlign: 'center', background: '#fff2f0' }}>
                    <Title level={2} style={{ color: '#ff4d4f', margin: 0 }}>
                      {applicationStats.rejected}
                    </Title>
                    <Text>Rejected</Text>
                  </Card>
                </Col>
              </Row>
            </div>
          )}

          {/* Submitted Forms */}
          <Title level={4}>Submitted Applications</Title>
          <ProfileFormsTable applications={applications} loading={loading} />
        </Card>
      </Col>

      {/* Edit Profile Modal */}
      <Modal
        title="Edit Profile"
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Save Changes"
        cancelText="Cancel"
        confirmLoading={updating}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="Display Name"
            name="displayName"
            rules={[{ required: true, message: "Please enter your name" }]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Enter your full name" 
            />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
          >
            <Input 
              placeholder="Email" 
              disabled 
              style={{ background: '#f5f5f5' }}
            />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[{ required: true, message: "Please enter your contact number" }]}
          >
            <Input placeholder="+1 234 567 8900" />
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
}